import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as StripePayoutsIcon } from '../../../assets/icons/sidebar/stripe-payout.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestStripePayoutsInterface,
  StripePayoutsInterface,
} from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchRejectedStripePayouts,
  fetchRejectedStripePayoutsMethodFilters,
  updateRejectedStripePayoutsFilters,
  disableRejectedStripePayoutsApplied,
  setSortData,
} from '../../../redux/slices/RejectedStripePayoutsSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ToFixedValue from '../../../shared-functions/ToFixedValue';

/**
 * Rejected stripe payouts components
 * @returns {any} The result
 */
function RejectedPartnerPayouts() {
  const { t } = useTranslation('stripePayout');
  const {
    payouts,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.rejectedStripePayouts);
  const dispatch = useAppDispatch();

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (!loading) {
      dispatch(fetchRejectedStripePayouts(data));
    }
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: RequestStripePayoutsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('revenueFromDate'),
      accessorKey: 'fromDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('revenueToDate'),
      accessorKey: 'toDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('payoutAmount'),
      accessorKey: 'amount' as keyof StripePayoutsInterface,
      sortAllow: true,
      /**
       * Payment amount view in table
       * @param {any} payoutsStatus - The payoutsStatus
       * @returns {any} The result
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div>{ToFixedValue(payoutsStatus.amount)}</div>
      ),
    },
    {
      label: t('payoutDate'),
      accessorKey: 'rejectedAt' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
  ];

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || payouts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRejectedStripePayoutsApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('partnerPayouts'),
        icon: <StripePayoutsIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-container">
      <CustomTable
        header={column}
        version="v2"
        data={payouts}
        serialNo
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateRejectedStripePayoutsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRejectedStripePayoutsMethodFilters}
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel="Refresh"
      />
    </div>
  );
}

export default RejectedPartnerPayouts;